import type { ReactNode } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import classnames from 'classnames';

import { getRadioStyle } from '../../../hotels-utils/components-hotel-card-adjacent/CardImageMobile';
import { withI18n } from '../../../hotels-utils/services/i18n';
import { getIds } from '../../../utils/a11y';

import BasicInfo from './BasicInfo';

import type { I18n } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './CardLayoutMobile.module.scss';

type CardLayoutMobileProps = {
  imageSlider?: ReactNode;
  ratingNode?: ReactNode;
  priceInfo?: ReactNode;
  otherPrices?: ReactNode;
  confidentMsg?: ReactNode;
  nameStarNode?: ReactNode;
  url?: string;
  AISummary?: ReactNode;
  onClick?: Function;
  distanceNode?: ReactNode;
  hotelCardAccessibility?: boolean;
  imageRadio?: string;
  index?: number;
  i18n: I18n;
  viewedHotelCard?: boolean;
  urlRel?: string;
};

const CardLayoutMobile = ({
  AISummary = undefined,
  confidentMsg = undefined,
  distanceNode = undefined,
  hotelCardAccessibility = undefined,
  i18n,
  imageRadio = undefined,
  imageSlider = undefined,
  index = undefined,
  nameStarNode = undefined,
  onClick = undefined,
  otherPrices = undefined,
  priceInfo = undefined,
  ratingNode = undefined,
  url = undefined,
  urlRel = undefined,
  viewedHotelCard = undefined,
}: CardLayoutMobileProps) => {
  const ratingNodes = (
    <div className={STYLES.CardLayoutMobile__ratingAndConfident}>
      {ratingNode}
      {confidentMsg}
    </div>
  );

  const style = { boxShadow: '0 1px 3px 0 rgba(37, 32, 31, 0.3' };
  const optionLabel =
    index === undefined
      ? undefined
      : i18n.translate(
          'aria_option',
          {
            number: index + 1,
          },
          '{',
          '}',
          true,
        );
  const headerIdPrefixs = [
    'hotel_name',
    'hotel_star',
    'hotel_price',
    'hotel_nights',
    'flight_cugs',
    'direct_discount',
    'hotel_partner',
    'hotel_original_price',
  ];

  const showHotelInfo =
    (viewedHotelCard && (ratingNode || confidentMsg || distanceNode)) ||
    !viewedHotelCard;

  return (
    <div className={classnames(STYLES.CardLayoutMobile)} style={style}>
      <div
        className={STYLES.CardLayoutMobile__image}
        {...getRadioStyle(imageRadio)}
        aria-hidden
      >
        {imageSlider}
      </div>
      <div
        className={classnames(STYLES.CardLayoutMobile__baseInfo, {
          [STYLES['CardLayoutMobile__baseInfo--accessibility']]:
            hotelCardAccessibility,
        })}
      >
        {showHotelInfo && (
          <BpkCard
            className={classnames(STYLES.CardLayoutMobile__card, {
              [STYLES['CardLayoutMobile__card--accessibility']]:
                hotelCardAccessibility,
            })}
            padded={false}
            href={url}
            target="_blank"
            data-test-id="hotel-card"
            rel={urlRel ? `opener ${urlRel}` : 'opener'}
            onClick={() => {
              if (onClick !== null)
                onClick?.(true, null, true, 'baseInfoOnMobile');
            }}
            role="link"
            aria-label={optionLabel}
          >
            {nameStarNode && (
              <div
                aria-labelledby={getIds(headerIdPrefixs, index)}
                role="heading"
                aria-level={3}
              >
                {nameStarNode}
              </div>
            )}

            {hotelCardAccessibility ? (
              <BasicInfo
                ratingNode={ratingNode}
                confidentMsg={confidentMsg}
                distanceNode={distanceNode}
                hotelCardAccessibility={hotelCardAccessibility}
                index={index}
              />
            ) : (
              <>
                {ratingNodes}
                {distanceNode}
              </>
            )}
            {AISummary}
            {priceInfo && (
              <div className={STYLES.CardLayoutMobile__priceInfo}>
                {priceInfo}
              </div>
            )}
            {otherPrices}
          </BpkCard>
        )}
      </div>
    </div>
  );
};

export default withI18n(CardLayoutMobile);
